import { DOCUMENT, inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter, startWith } from 'rxjs/operators';
import { environment } from '@env/environment';
import { routeTranslations } from '../route-translations';
import { splitLangPrefix } from '../lang-prefix';

type RouteMap = Record<string, string>;

const ROUTES_PREFIX = 'ROUTES.';

/**
 * Keeps `<link rel="canonical">` and per-language `<link rel="alternate" hreflang>`
 * (incl. `x-default`) in the document head in sync with the active route.
 *
 * Hrefs are emitted as root-relative paths (e.g. `/en/about-us`) so the same
 * markup is correct on every deploy and prerendered routes don't bake the SSG
 * synthetic host into the static HTML. Crawlers resolve them against the
 * document URL.
 *
 * Routes are localized via the `ROUTES.*` translation keys, so the equivalent URL
 * in another language is derived by reverse-mapping each translated segment back
 * to its key and re-translating it. Route params / slugs / page numbers are not
 * `ROUTES.*` values, so they pass through unchanged.
 */
@Injectable({
    providedIn: 'root',
})
export class SeoAlternateLinksService {
    private router = inject(Router);
    private document = inject(DOCUMENT);

    // lang -> { bareKey: translatedSegment }
    private forward = new Map<string, RouteMap>();
    // lang -> { translatedSegment: bareKey }
    private reverse = new Map<string, RouteMap>();

    private canonicalOverride: string | null = null;

    init(): void {
        this.buildMaps();
        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                startWith(null),
            )
            .subscribe(() => this.updateLinks());
    }

    private buildMaps(): void {
        for (const lang of environment.supportedLanguages) {
            const json = routeTranslations(lang);
            const fwd: RouteMap = {};
            const rev: RouteMap = {};

            for (const key of Object.keys(json)) {
                if (!key.startsWith(ROUTES_PREFIX)) {
                    continue;
                }
                const bare = key.slice(ROUTES_PREFIX.length);
                const value = json[key];
                // Only single-segment, slug-safe values participate in URL building.
                if (!value || value.includes('/') || value.includes(' ')) {
                    continue;
                }
                fwd[bare] = value;
                rev[value] = bare;
            }

            this.forward.set(lang, fwd);
            this.reverse.set(lang, rev);
        }
    }

    private localizedPath(targetLang: string, currentLang: string, restSegments: string[]): string {
        const rev = this.reverse.get(currentLang) ?? {};
        const fwd = this.forward.get(targetLang) ?? {};
        const mapped = restSegments.map((segment) => {
            const bare = rev[segment];
            if (bare === undefined) {
                return segment;
            }
            return fwd[bare] ?? bare;
        });
        return ['', targetLang, ...mapped].join('/');
    }

    /**
     * Per-page canonical override, as a root-relative localized path
     * (e.g. `/pl/produkty/wszystkie/295`). For pages reachable under more than
     * one URL (duplicate content), so canonical and the hreflang alternates
     * point at the preferred variant instead of the current URL. Pages must
     * clear it (`null`) on destroy.
     *
     * Re-renders immediately: a routed component's `ngOnInit` runs after
     * `NavigationEnd`, so waiting for the next navigation event would be too late.
     */
    setCanonicalPath(path: string | null): void {
        this.canonicalOverride = path;
        this.updateLinks();
    }

    private updateLinks(): void {
        const url = this.canonicalOverride ?? this.router.url.split('#')[0];
        const path = url.split('?')[0];
        const segments = path.split('/').filter(Boolean);
        if (segments.length === 0) {
            return;
        }

        const { lang: currentLang, rest } = splitLangPrefix(segments);

        this.upsertLink('canonical', null, url);

        for (const lang of environment.supportedLanguages) {
            const href = this.localizedPath(lang, currentLang, rest);
            this.upsertLink('alternate', lang, href);
            if (lang === environment.defaultLanguage) {
                this.upsertLink('alternate', 'x-default', href);
            }
        }
    }

    private upsertLink(rel: string, hreflang: string | null, href: string): void {
        const head = this.document.head;
        const selector = hreflang ? `link[rel="${rel}"][hreflang="${hreflang}"]` : `link[rel="${rel}"]:not([hreflang])`;
        let link = head.querySelector(selector);

        if (!link) {
            link = this.document.createElement('link');
            link.setAttribute('rel', rel);
            if (hreflang) {
                link.setAttribute('hreflang', hreflang);
            }
            head.appendChild(link);
        }

        link.setAttribute('href', href);
    }
}
